import { Fragment } from 'react';
import type { UseFormSetError } from 'react-hook-form';

import {
  applicantEmailValidate,
  applicantEmailsUniqueByRowValidate,
  type DateLinkingConfig,
  type FieldEventFormApi,
  type FormSlots,
  type LeaseDetailsFormConfig,
} from '@/modules/application-tracker/components/lease-form';
import type { AddressAutocompleteOption } from '@/utils/types/addressAutocompleteOption.interface';
import type { Renter } from '@/utils/types/renter.interface';

import { InviteEligibilityApplicantNotice } from './content/invite-eligibility-applicant-notice';
import { InviteEligibilityController } from './content/invite-eligibility-controller';
import { CoverageRequirementsAlert } from './content/invite-lease-details.drawer.coverage-requirements-alert';
import { InviteLeaseDetailsFormDivider } from './components/invite-lease-details-form-divider';
import {
  InviteLeaseDetailsSegmentEvents,
  INVITE_LEASE_DETAILS_VALIDATION_MESSAGES as MSG,
} from './constants';
import { SecurityDepositTypes } from '@/constants/CoverageType';
import {
  addressValidate,
  apartmentNumberValidate,
  leaseEndValidate,
  leaseStartValidate,
} from './invite-lease-details.field-schemas';
import { INVITE_RENT_FIELDS } from './invite-lease-details.rent-fields';
import type { InviteLeaseDetailsFormValues } from './invite-lease-details.form-values';
import type { InviteLeaseDetailsPresetDeps } from './invite-lease-details.preset-deps';
import {
  buildInviteCoverageSection,
  buildInviteScreeningSection,
} from './invite-lease-details.sections';
import { InviteLeaseDetailsSharedFooter } from './invite-lease-details.shared-footer';
import { getApplicantEmailValidationResult, mapInviteFormValuesToRenter } from './utils';

// The single place that knows about invite's variant flags (SMB / PMI / new
// coverages / customizable coverage). It maps `(user, flags)` → a config the
// shared engine renders; the engine itself stays variant-blind. Submit reuses
// the existing controller pipeline by mapping engine values back to `Renter`.

export type { InviteLeaseDetailsPresetDeps } from './invite-lease-details.preset-deps';

/**
 * The submit pipeline (`applyInviteServiceFieldErrors`, the controller's
 * applicant error fan-out) is shared with the legacy form, which keys
 * applicants under `applicantEmails.X.value`. The shared engine stores them
 * under `applicants.X.email`. This is the one diverging path; every other
 * field name in `API_TO_FORM_FIELD` already uses engine-matching names, so
 * they need no translation.
 */
export const legacyApplicantPathToEngine = (path: string): string => {
  const match = /^applicantEmails\.(\d+)\.value$/.exec(path);
  return match ? `applicants.${match[1]}.email` : path;
};

/**
 * A property without SDR hides the deposit section, so drop any amount typed for a previous
 * property and fall back to the company default.
 */
export const applyInviteAddressSelection = (
  option: Pick<AddressAutocompleteOption, 'is_sdr_enrolled'> | null | undefined,
  form: Pick<FieldEventFormApi, 'setValue'>,
): void => {
  if (option?.is_sdr_enrolled === false) {
    form.setValue('securityDepositCoverage', SecurityDepositTypes.CompanyDefault);
    form.setValue('securityDepositReplacementAmountOverride', null);
  }
};

// Invite's default term is one year from the chosen start, so a new start always re-derives the
// end — even one the user edited.
export const INVITE_DATE_LINKING: DateLinkingConfig = {
  startField: 'leaseStartDate',
  endField: 'leaseEndDate',
  leaseDurationMonths: 12,
  valueFormat: 'iso',
  overwriteManualEnd: true,
};

const LEASE_INFORMATION_SUBTITLE =
  "Enter the lease details below. If your applicant is paying for the entire first month upfront at lease signing, we don't need to cover it (optional).";

export const inviteLeaseDetailsPreset = (
  deps: InviteLeaseDetailsPresetDeps,
): LeaseDetailsFormConfig => {
  const {
    isSmbInvite,
    isPmi,
    newCoveragesEnabled,
    customCoverageDisabled,
    trackInvite,
    onMilestoneReached,
    onSubmit,
  } = deps;

  const slots: FormSlots = {
    top: (
      <Fragment>
        {customCoverageDisabled ? <CoverageRequirementsAlert /> : null}
        {/* Inside FormProvider: runs the debounced eligibility query + block field errors. */}
        <InviteEligibilityController />
      </Fragment>
    ),
    // The footer also shows the submit error, read from the shared form's runtime context so a
    // new message doesn't rebuild this config.
    bottom: <InviteLeaseDetailsSharedFooter />,
  };

  return {
    sections: {
      leaseInformation: {
        visible: true,
        title: 'Lease information',
        subtitle: LEASE_INFORMATION_SUBTITLE,
        fields: {
          address: {
            kind: 'address',
            visible: true,
            required: true,
            label: 'Search by address or property name',
            validate: addressValidate,
            events: {
              onChange: ({ value, form }) => {
                const option = value as AddressAutocompleteOption | null;
                applyInviteAddressSelection(option, form);
                if (option?.monolith_building_id) {
                  onMilestoneReached?.('hadPropertySelected');
                  trackInvite(InviteLeaseDetailsSegmentEvents.PropertySelected, {
                    property_id: option.property_id,
                  });
                }
              },
            },
          },
          unit: {
            kind: 'text',
            visible: true,
            required: true,
            requiredMessage: MSG.UNIT_NUMBER_REQUIRED,
            label: 'Unit number',
            validate: apartmentNumberValidate,
          },
          leaseStartDate: {
            kind: 'date',
            visible: true,
            required: true,
            requiredMessage: MSG.LEASE_START_REQUIRED,
            invalidDateMessage: MSG.LEASE_START_INVALID,
            rowGroup: 'lease-dates',
            label: 'Lease start date',
            minDateOffsetDays: 365,
            // Mirrors `leaseStartValidate`'s `max-one-year-future`: ~1y forward.
            // The validator still owns the precise edge (- 1 day); this is the
            // UI hint, restoring the legacy picker's maxDate behavior.
            maxDateOffsetDays: 365,
            validate: leaseStartValidate(),
          },
          leaseEndDate: {
            kind: 'date',
            visible: true,
            required: true,
            requiredMessage: MSG.LEASE_END_REQUIRED,
            invalidDateMessage: MSG.LEASE_END_INVALID,
            rowGroup: 'lease-dates',
            label: 'Lease end date',
            minDateOffsetDays: 365,
            // Mirrors `leaseEndValidate`'s `max-eleven-years-future`: ~11y forward.
            maxDateOffsetDays: 365 * 11,
            validate: leaseEndValidate(newCoveragesEnabled),
          },
          ...INVITE_RENT_FIELDS,
        },
      },
      applicants: {
        visible: true,
        title: 'Applicant contact information',
        subtitle:
          "Enter your applicant's email address below. You can add up to five additional applicants, if applicable.",
        min: 1,
        max: 6,
        primaryLocked: false,
        addButtonLabel: 'Add additional applicant',
        // Flag-gated inside the notice; renewal/CLD presets omit this slot.
        renderEntryNotice: (index) => <InviteEligibilityApplicantNotice index={index} />,
        compactEmailLabels: {
          primary: 'Primary Applicant Email Address',
          additional: 'Additional Applicant Email Address',
        },
        // Row-level so the duplicate message renders under the later email.
        // The array-level check only sets `errors.applicants`, which no input displays.
        arrayValidate: applicantEmailsUniqueByRowValidate,
        onAdd: ({ count, index }) =>
          trackInvite(InviteLeaseDetailsSegmentEvents.AdditionalApplicantChanged, {
            action: 'add',
            applicant_count: count,
            applicant_index: index,
          }),
        onRemove: ({ count, index }) =>
          trackInvite(InviteLeaseDetailsSegmentEvents.AdditionalApplicantChanged, {
            action: 'remove',
            applicant_count: count,
            applicant_index: index,
          }),
        fields: {
          email: {
            kind: 'text',
            visible: true,
            required: true,
            requiredMessage: MSG.EMAIL_REQUIRED,
            defaultValue: '',
            label: 'Applicant Email Address',
            validate: applicantEmailValidate,
            events: {
              onBlur: ({ arrayIndex, valid, getValues }) => {
                const index = arrayIndex ?? 0;
                const applicants = (getValues('applicants') as Array<{ email?: string }>) ?? [];
                const applicantEmails = applicants.map(({ email }) => ({ value: email ?? '' }));
                trackInvite(InviteLeaseDetailsSegmentEvents.EmailValidationCompleted, {
                  field_name: `applicantEmails.${index}.value`,
                  applicant_index: index,
                  validation_result: getApplicantEmailValidationResult(
                    applicantEmails,
                    index,
                    valid,
                  ),
                  validation_source: 'client',
                });
              },
            },
          },
        },
      },
      screening: buildInviteScreeningSection(deps),
      coverage: buildInviteCoverageSection(deps),
    },
    submit: {
      // `selectedSmbPricing` is a companion of the SMB tier option-box, kept in form state.
      buildPayload: (values) =>
        mapInviteFormValuesToRenter(values as InviteLeaseDetailsFormValues, {
          isSmbInvite,
          isPmi,
        }),
      submit: (payload, ctx) => {
        onMilestoneReached?.('hadSubmitAttempt');
        // The submit pipeline (controller + `applyInviteServiceFieldErrors`) is
        // shared with the legacy form, which stores applicants at
        // `applicantEmails.X.value`. The shared engine stores them at
        // `applicants.X.email`. Translate legacy applicant paths to engine
        // paths so server-side per-applicant errors land on the right field;
        // everything else in `API_TO_FORM_FIELD` already uses engine-matching
        // names (leaseStartDate, unit, screeningStatus, etc.).
        const adaptedSetError: UseFormSetError<Renter> = (name, error, options) => {
          const enginePath = legacyApplicantPathToEngine(String(name));
          ctx.setError(enginePath, error, options);
        };
        return onSubmit(payload as Renter, adaptedSetError, ctx.attemptNumber);
      },
    },
    dateLinking: INVITE_DATE_LINKING,
    ui: {
      hideDefaultActions: true,
      inputSize: 'small',
      sectionDivider: <InviteLeaseDetailsFormDivider />,
      slots,
    },
  };
};
